import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "./translations";

// Ordered list of supported locales — matches the 14 Play Store locales.
// `name` is the native language name shown in the switcher.
export const LOCALES = [
  { code: "en", name: "English" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "es", name: "Español" },
  { code: "pt-BR", name: "Português (BR)" },
  { code: "hi", name: "हिन्दी" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "ru", name: "Русский" },
  { code: "tr", name: "Türkçe" },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "th", name: "ไทย" },
  { code: "ar", name: "العربية" },
];

const RTL_LOCALES = new Set(["ar"]);
const STORAGE_KEY = "mintlab_locale";
const DEFAULT_LOCALE = "en";

const SUPPORTED = new Set(LOCALES.map((l) => l.code));

// Map a browser language tag (e.g. "pt-BR", "es-419", "zh-Hans") to one of
// our supported codes. Falls back to English when there's no match.
export function resolveLocale(input) {
  if (!input) return DEFAULT_LOCALE;
  const tag = String(input);
  if (SUPPORTED.has(tag)) return tag;
  const primary = tag.toLowerCase().split("-")[0];
  if (primary === "pt") return "pt-BR";
  const match = LOCALES.find((l) => l.code.toLowerCase().split("-")[0] === primary);
  return match ? match.code : DEFAULT_LOCALE;
}

function detectInitialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.has(saved)) return saved;
  } catch (_) {}
  const langs = (typeof navigator !== "undefined" && (navigator.languages || [navigator.language])) || [];
  for (const l of langs) {
    const resolved = resolveLocale(l);
    if (resolved !== DEFAULT_LOCALE || String(l).toLowerCase().startsWith("en")) return resolved;
  }
  return DEFAULT_LOCALE;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  const dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
    document.documentElement.setAttribute("dir", dir);
  }, [locale, dir]);

  const setLocale = useCallback((code) => {
    if (!SUPPORTED.has(code)) return;
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
    setLocaleState(code);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = translations[locale] || translations[DEFAULT_LOCALE];
      let str = dict[key];
      if (str == null) str = translations[DEFAULT_LOCALE][key];
      if (str == null) return key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          str = str.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]);
        }
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t, dir }), [locale, setLocale, t, dir]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

// Convenience hook returning just the translate function.
export function useT() {
  return useI18n().t;
}
